import { useCallback, useEffect, useState } from "react";
import { Eye, Globe2, Loader2, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { AdminPageHeader } from "./components/AdminUi";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE_URL = `${API}/api/admin/commerciale/richieste-sito-rco`;
const REQUEST_TYPES = { contatto: "Contatto", demo: "Richiesta demo" };
const REQUEST_STATUSES = ["Nuovo", "In lavorazione", "Gestito"];

function statusClass(status) {
  if (status === "Gestito") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (status === "In lavorazione") return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-blue-100 text-blue-800 border-blue-200";
}

function typeClass(type) {
  return type === "demo"
    ? "bg-violet-100 text-violet-800 border-violet-200"
    : "bg-cyan-100 text-cyan-800 border-cyan-200";
}

function displayDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("it-IT");
}

function errorMessage(payload, fallback) {
  return typeof payload?.detail === "string" ? payload.detail : fallback;
}

export default function RcoWebsiteRequestsAdminModule() {
  const token = localStorage.getItem("super_admin_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [updatingId, setUpdatingId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query.trim()) params.set("q", query.trim());
      if (type) params.set("form_type", type);
      if (status) params.set("stato", status);
      const response = await fetch(`${BASE_URL}?${params.toString()}`, { headers });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(errorMessage(payload, "Impossibile caricare le richieste"));
      setItems(payload.data || []);
    } catch (error) {
      toast.error(error.message || "Impossibile caricare le richieste");
    } finally {
      setLoading(false);
    }
  }, [query, status, type]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (request, nextStatus) => {
    setUpdatingId(request.id);
    try {
      const response = await fetch(`${BASE_URL}/${request.id}/stato`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ stato: nextStatus }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(errorMessage(payload, "Aggiornamento stato non riuscito"));
      setItems((current) => current.map((item) => (
        item.id === request.id ? { ...item, stato: nextStatus } : item
      )));
      setSelected((current) => (
        current?.id === request.id ? { ...current, stato: nextStatus } : current
      ));
      window.dispatchEvent(new Event("affidya-commerciale-menu-counts"));
    } catch (error) {
      toast.error(error.message || "Aggiornamento stato non riuscito");
    } finally {
      setUpdatingId("");
    }
  };

  return (
    <div className="space-y-4">
      <AdminPageHeader
        icon={Globe2}
        title="Richieste sito R&CO."
        description="Contatti e richieste demo ricevuti da rco-group.it."
        actions={
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aggiorna
          </Button>
        }
      />

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[220px] flex-1">
          <Label className="text-xs">Cerca</Label>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              className="pl-8"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Nome, email, organizzazione, messaggio…"
            />
          </div>
        </div>
        <div className="w-44">
          <Label className="text-xs">Tipologia</Label>
          <Select value={type || "__all__"} onValueChange={(value) => setType(value === "__all__" ? "" : value)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutte</SelectItem>
              <SelectItem value="contatto">Contatti</SelectItem>
              <SelectItem value="demo">Richieste demo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-44">
          <Label className="text-xs">Stato</Label>
          <Select value={status || "__all__"} onValueChange={(value) => setStatus(value === "__all__" ? "" : value)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutti gli stati</SelectItem>
              {REQUEST_STATUSES.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="admin-table-sticky-actions w-full text-sm">
            <thead className="border-b bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="p-3">Ricevuta</th>
                <th className="p-3">Tipologia</th>
                <th className="p-3">Contatto</th>
                <th className="p-3">Organizzazione / prodotto</th>
                <th className="p-3">Stato</th>
                <th className="p-3">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan="6" className="p-8 text-center text-slate-500"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Caricamento…</td></tr>
              ) : !items.length ? (
                <tr><td colSpan="6" className="p-8 text-center text-slate-500">Nessuna richiesta trovata.</td></tr>
              ) : items.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50">
                  <td className="whitespace-nowrap p-3 text-xs text-slate-500">{displayDate(item.created_at)}</td>
                  <td className="p-3"><Badge className={typeClass(item.form_type)}>{REQUEST_TYPES[item.form_type] || item.form_type}</Badge></td>
                  <td className="p-3"><p className="font-medium">{item.name}</p><p className="text-xs text-slate-500">{item.email}{item.phone ? ` · ${item.phone}` : ""}</p></td>
                  <td className="p-3"><p>{item.organization || "—"}</p><p className="text-xs text-slate-500">{item.product_interest || item.organization_type || "—"}</p></td>
                  <td className="p-3">
                    <Select value={item.stato || "Nuovo"} onValueChange={(value) => updateStatus(item, value)} disabled={updatingId === item.id}>
                      <SelectTrigger className="h-8 w-36"><Badge className={statusClass(item.stato || "Nuovo")}>{item.stato || "Nuovo"}</Badge></SelectTrigger>
                      <SelectContent>{REQUEST_STATUSES.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="p-3"><Button size="icon" variant="ghost" title="Vedi tutti i dati" onClick={() => setSelected(item)}><Eye className="h-4 w-4" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      <Dialog open={!!selected} onOpenChange={(open) => { if (!open) setSelected(null); }}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>{REQUEST_TYPES[selected?.form_type] || "Richiesta"} · {selected?.name}</DialogTitle></DialogHeader>
          {selected ? (
            <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
              <Detail label="Ricevuta" value={displayDate(selected.created_at)} />
              <Detail label="Stato" value={selected.stato || "Nuovo"} />
              <Detail label="Nome" value={selected.name} />
              <Detail label="Email" value={selected.email} />
              <Detail label="Telefono" value={selected.phone} />
              <Detail label="Organizzazione" value={selected.organization} />
              <Detail label="Ruolo" value={selected.role} />
              <Detail label="Tipologia organizzazione" value={selected.organization_type} />
              <Detail label="Prodotto di interesse" value={selected.product_interest} />
              <Detail label="Consenso marketing" value={selected.marketing_consent ? "Sì" : "No"} />
              <Detail label="IP sorgente" value={selected.ip} />
              <div className="sm:col-span-2"><Detail label="Messaggio" value={selected.message} /></div>
            </div>
          ) : null}
          <DialogFooter><Button variant="outline" onClick={() => setSelected(null)}>Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 whitespace-pre-wrap text-slate-800">{value || "—"}</p>
    </div>
  );
}
